import "./styles/index.css";
import { createClientApp, kernelEmbeds } from "@atomic/app-shell";
import { BomExplorerEmbed } from "@atomic/bom-explorer-adapter/react";
import { ChecksEmbed } from "@atomic/checks-adapter/react";
import { DigitalTwinEmbed } from "@atomic/digital-twin-adapter/react";
import { LinearEmbed } from "@atomic/linear-adapter/react";
import { UserAdminEmbed } from "@atomic/user-admin-adapter/react";
import { APP_ENV } from "./app_env";
import { COMPANY_CODE } from "./client_identity";
import { useNucleusHost } from "./nucleusHost";
import nucleusVariables from "./nucleus/variables.json";
import { planReviewPathFor } from "./nucleusPlanReviewPath";
import { LmntProviders } from "./providers";
import { ENGINE_PAGES } from "./enginePages";
import { ROUTE_REGISTRY_EXTRAS } from "./routeRegistry";
import { FEATURES, NAV_EXTRAS, TOOL_OVERRIDES } from "./rail";
import { PRODUCT_ROUTES } from "./routes";

/**
 * Local dev only: the embeds a developer has pointed at a `vite dev` of their
 * core package on this machine (see .env.example). Only the variables that are
 * set become keys, so with no .env.local — the default state — the shell sees
 * an empty map and frames the minted page. The master-data override is read
 * by its own LMNT wrapper (embeds/local_embed_url.ts).
 */
function localEmbedUrls(): Partial<Record<"digital-twin" | "bom-explorer", string>> {
  const urls = {
    "digital-twin": import.meta.env.VITE_DIGITAL_TWIN_EMBED_URL,
    "bom-explorer": import.meta.env.VITE_BOM_EXPLORER_EMBED_URL,
  };
  return Object.fromEntries(Object.entries(urls).filter(([, url]) => Boolean(url)));
}

/**
 * The app is the client app shell (@atomic/app-shell) plus LMNT's own pages.
 * The shell owns sign-in and the session (its `fetchWithToken` is what the
 * Apollo client sends through — providers.tsx), the rail and the account menu,
 * the Nucleus dock mount and its route registry, feedback, activity logging,
 * `/digital-twin`, `/admin` (core's user-admin surface, View As beside it for
 * Atomic staff), `/checks` (core's tests + data validations: ROOT only, no tile,
 * which Nucleus links for a ROOT user) and the full-page `/nucleus`. This file is the manifest, the
 * adapters it installs, the Engine tiles, and LMNT's routes (routes.tsx).
 *
 * Features: Data, Reporting, Digital Twin and Nucleus are live. Tracker,
 * Allocation and Linear stay off the rail (the shell's default for them) —
 * LMNT reaches /trackers, /allocation-engine and /linear by direct navigation,
 * through its own routes. Docs is routed but has no tile (`toolOverrides`), as
 * the old rail had it. MRP is live with one view, core's BOM Explorer, on the
 * shell's EmbedPage over the adapter registered below (containers/MrpView).
 *
 * What the rail no longer carries: the "secret tile" dev-unlock combo the old
 * Sidebar could camouflage a module behind (five quick clicks on a locked
 * tile). No tile declared one, so nothing is lost today; a shell that wants it
 * back needs a per-tile render hook (candidate app-shell change).
 */
createClientApp({
  manifest: {
    companyCode: COMPANY_CODE,
    displayName: "LMNT",
    // The client's own email domains, as core's client manifest records them
    // (api/src/clients/lmnt.json emailDomains): Anonymize rewrites `drinklmnt` in an
    // address the way it rewrites the name. brainforge.ai is a partner's; left alone.
    emailDomains: ["drinklmnt.com", "brainforge.ai"],
    logo: "/images/lmnt-logo.png",
    logoDark: "/images/lmnt-logo-dark.png",
    // The rail shows the box alone, without "zero sugar hydration" (Jeff, 2026-10-26); the
    // sign-in card keeps the wordmark. Both drawings cropped from the wordmark files.
    mark: "/images/lmnt-mark.png",
    markDark: "/images/lmnt-mark-dark.png",
    // Planners land on their plan: Sparkling for everyone, Drink Mix for its planners
    // (named here until planners carry a product line of their own).
    homePath: "/sparkling",
    homePathByUser: { "magaly@drinklmnt.com": "/drink-mix", "julie@drinklmnt.com": "/drink-mix" },
    features: FEATURES,
    // Nucleus admission is the gateway's (NUCLEUS_ADMISSION=all), which the shell reads.
    // User writes are core's (the user-admin embed); the shell's AdminPage
    // mounts it over the kernel's `getUserAdminEmbedToken`. Not a role gate:
    // the shell shows User Admin to ADMIN and ROOT (its `isFullApp`), and the
    // embed lets an ADMIN invite the non-root roles, as LMNT's own page did.
    userAdministration: "staff-only",
  },
  env: APP_ENV,
  atomicLogo: "/atomic-logo-blue.png",
  product: {
    // The shared shell owns the bell, preferences and authenticated transport.
    // Always on, as Core's new-client template; there is no deployment flag.
    // Hiding the bell is a code change here plus a rebuild.
    notifications: { enabled: true },
    // Only the adapters the shell's own pages mount. The master-data,
    // queryviz, tracker and allocation adapters are mounted by LMNT's
    // wrappers (routes.tsx says why), which mint through Apollo as before —
    // registering them here would make the shell add its generic /data,
    // /reporting and friends ahead of those wrappers.
    embeds: kernelEmbeds(
      {
        "digital-twin": DigitalTwinEmbed,
        linear: LinearEmbed,
        "user-admin": UserAdminEmbed,
        "bom-explorer": BomExplorerEmbed,
        // The shell adds /checks for it (ROOT only, no tile); the gateway mints
        // getChecksEmbedToken (backendgateway/.../checks).
        checks: ChecksEmbed,
      },
      {},
      // Local dev only: a `vite dev` of an embed's core package on this
      // machine, framed in place of the minted page. From the gitignored
      // .env.local; the shell honours a local origin only, and a production
      // build drops it.
      import.meta.env.DEV ? localEmbedUrls() : {},
      import.meta.env,
    ),
    routes: PRODUCT_ROUTES,
    // The Engine tiles and the tile overrides: rail.ts, which the route
    // registry test builds its nav from too.
    navExtras: NAV_EXTRAS,
    toolOverrides: TOOL_OVERRIDES,
    routeRegistry: ROUTE_REGISTRY_EXTRAS,
    // The pages that show Replenishment runs, with the controls Nucleus may set on
    // them (enginePages.ts); the shell merges each onto its registry entry.
    enginePages: ENGINE_PAGES,
    // The Nucleus dock is core's standard one (@atomic/app-shell mounts it); LMNT adds
    // only what this app knows — its tool vocabulary (src/nucleus/variables.json), its
    // /demand report host, unsaved work (nucleusHost.ts). The run on the page is
    // published by the run selector (useRunSelector), and the shell's dock sends it.
    // Plan Review opens on the planning page the plan concerns (the class's
    // Replenishment page), which hosts core's review-only embed — not on
    // /allocation-engine, which is direct-nav only (nucleusPlanReviewPath.ts).
    nucleus: { toolVariables: nucleusVariables, useHost: useNucleusHost, planReviewPath: planReviewPathFor },
    wrap: (app) => <LmntProviders>{app}</LmntProviders>,
  },
}).mount();
